import { ThemeProvider, useTheme } from '@mui/material/styles';
import { createElement, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { createRoot, type Root } from 'react-dom/client';
import {
  UNSAFE_DataRouterContext,
  UNSAFE_DataRouterStateContext,
  type Blocker,
} from 'react-router';
import { ConfirmDialog } from '../overlay/dialogs/confirm-dialog/confirm-dialog';
import { Typography } from '../primitives/typography/typography';

type UseUnsavedChangesGuardOptions = {
  enabled: boolean;
  message?: string;
  shouldBlock?: () => boolean;
};

const DEFAULT_MESSAGE = 'You have unsaved changes. If you leave now, your changes will be lost.';
const IDLE_BLOCKER: Blocker = {
  state: 'unblocked',
  reset: undefined,
  proceed: undefined,
  location: undefined,
};

let blockerId = 0;

// React-router consults only the last-registered blocker, so each one answers for every guard here.
const guardsByRouter = new WeakMap<object, Map<string, () => boolean>>();

function getGuardRegistry(router: object): Map<string, () => boolean> {
  let registry = guardsByRouter.get(router);
  if (!registry) {
    registry = new Map();
    guardsByRouter.set(router, registry);
  }
  return registry;
}

function useOptionalBlocker(shouldBlock: () => boolean): Blocker {
  const dataRouterContext = useContext(UNSAFE_DataRouterContext);
  const dataRouterStateContext = useContext(UNSAFE_DataRouterStateContext);
  const [blockerKey, setBlockerKey] = useState('');

  // Ensure router always calls the latest `shouldBlock`, without waiting on the effect to re-run.
  const shouldBlockRef = useRef(shouldBlock);
  shouldBlockRef.current = shouldBlock;

  useEffect(() => {
    if (!dataRouterContext) return;

    const key = String(++blockerId);
    const registry = getGuardRegistry(dataRouterContext.router);
    registry.set(key, () => shouldBlockRef.current());
    setBlockerKey(key);

    return () => {
      registry.delete(key);
      dataRouterContext.router.deleteBlocker(key);
    };
  }, [dataRouterContext]);

  useEffect(() => {
    if (!dataRouterContext || !blockerKey) return;

    const registry = getGuardRegistry(dataRouterContext.router);
    dataRouterContext.router.getBlocker(blockerKey, () =>
      Array.from(registry.values()).some((guardShouldBlock) => guardShouldBlock()),
    );
  }, [dataRouterContext, blockerKey]);

  if (!blockerKey || !dataRouterStateContext?.blockers.has(blockerKey)) return IDLE_BLOCKER;

  return dataRouterStateContext.blockers.get(blockerKey) ?? IDLE_BLOCKER;
}

export function useUnsavedChangesGuard({
  enabled,
  message,
  shouldBlock,
}: UseUnsavedChangesGuardOptions) {
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  const isBlockingEnabled = useCallback(() => shouldBlock?.() ?? enabledRef.current, [shouldBlock]);
  const blocker = useOptionalBlocker(isBlockingEnabled);
  const theme = useTheme();
  const dialogRootRef = useRef<Root | null>(null);
  const dialogContainerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    // Browsers only allow native beforeunload prompts for hard reload/close/tab-exit.
    // We can style in-app route navigation prompts, but unload confirmation stays native.
    if (!isBlockingEnabled()) return;

    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!isBlockingEnabled()) return;
      event.preventDefault();
    };

    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
    };
  }, [isBlockingEnabled]);

  useEffect(() => {
    if (blocker.state !== 'blocked') {
      dialogRootRef.current?.unmount();
      dialogRootRef.current = null;

      if (dialogContainerRef.current) {
        dialogContainerRef.current.remove();
        dialogContainerRef.current = null;
      }
      return;
    }

    if (!dialogContainerRef.current) {
      const container = document.createElement('div');
      document.body.appendChild(container);
      dialogContainerRef.current = container;
      dialogRootRef.current = createRoot(container);
    }

    dialogRootRef.current?.render(
      createElement(
        ThemeProvider,
        { theme },
        createElement(
          ConfirmDialog,
          {
            open: true,
            title: 'Unsaved changes',
            cancelLabel: 'Stay',
            submitLabel: 'Leave anyway',
            onCancel: () => blocker.reset(),
            onSubmit: () => blocker.proceed(),
            disableDefaultEndIcon: true,
          },
          createElement(Typography.Body, { align: 'center' }, message ?? DEFAULT_MESSAGE),
        ),
      ),
    );

    return () => {
      dialogRootRef.current?.unmount();
      dialogRootRef.current = null;

      if (dialogContainerRef.current) {
        dialogContainerRef.current.remove();
        dialogContainerRef.current = null;
      }
    };
  }, [blocker, blocker.state, message, theme]);

  return { blocker };
}
